import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { API, useAuth, useTheme } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DrishtiPaywallGate from "@/components/drishti/DrishtiPaywallGate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  BookMarked, Plus, TrendingUp, TrendingDown, Target, Trophy,
  BarChart3, AlertTriangle, X, ChevronRight, Clock, DollarSign,
  ArrowUpRight, ArrowDownRight, Percent, Activity, Filter,
  Edit3, Trash2, Eye, CheckSquare, Brain, MessageSquare, Image,
  Zap, Flame, Crosshair, TrendingUp as TrendUp, Link2, Wifi,
  Star, AlertCircle, RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const SETUP_CATEGORIES = ["breakout", "pullback", "reversal", "range", "news", "gap", "trend_following", "mean_reversion"];
const MISTAKE_TAGS = ["fomo", "revenge", "oversize", "no_sl", "moved_sl", "early_exit", "late_entry", "ignored_plan", "emotional", "overtrading"];
const TIMEFRAMES = ["1m", "5m", "15m", "1h", "4h", "Daily", "Weekly"];
const INSTRUMENTS = ["equity", "futures", "options", "crypto", "forex"];
const EXIT_REASONS = ["target_hit", "sl_hit", "manual", "trailing_sl", "time_based", "news_event"];
const PRE_TRADE_CHECKLIST_ITEMS = [
  "Trend identified", "Zone marked on chart", "Risk-reward > 1:1.5",
  "Position size calculated", "Stop loss set", "Multiple TF confirmed",
  "No major news event", "Emotional state calm"
];

const IMPROVEMENT_TIPS = {
  fomo: "Wait for price to come to your level — patience is an edge.",
  revenge: "After a loss, step away for 30 minutes before placing the next trade.",
  oversize: "Risk no more than 1% of capital per trade to protect your account.",
  no_sl: "Always set your stop loss before entering — not after.",
  moved_sl: "Set SL once and don't touch it — let the trade breathe.",
  early_exit: "Trust your target level — let winners run.",
  late_entry: "If the setup is gone, skip it. The next one is always coming.",
  ignored_plan: "Stick to the plan you made before the market opened.",
  emotional: "Trade the chart, not your feelings.",
  overtrading: "Quality over quantity — 2 great trades beat 10 average ones.",
};

// ── Helpers ─────────────────────────────────────────────────────────────────
function fmtPnl(pnl) {
  if (pnl == null) return "—";
  const sign = pnl >= 0 ? "+" : "";
  return `${sign}₹${Math.abs(pnl).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}
function fmtPct(pct) {
  if (pct == null) return "";
  return `${pct >= 0 ? "+" : ""}${pct}%`;
}
function fmtPrice(num) {
  if (!num) return "—";
  return Number(num).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
function tradeDuration(entry, exit) {
  if (!entry || !exit) return null;
  const ms = new Date(exit) - new Date(entry);
  const hours = Math.floor(ms / 3600000);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h`;
  return `${Math.floor(ms / 60000)}m`;
}

// ── Compute live trade status (active trades only) ─────────────────────────
function getLiveTradeStatus(trade, cmp) {
  if (!cmp || trade.status !== "active") return null;
  const isLong = trade.trade_type === "long";
  if (isLong && cmp >= trade.target) return { label: "TARGET HIT", color: "#00FF88", bg: "rgba(0,255,136,0.12)", icon: "🎯" };
  if (!isLong && cmp <= trade.target) return { label: "TARGET HIT", color: "#00FF88", bg: "rgba(0,255,136,0.12)", icon: "🎯" };
  if (isLong && cmp <= trade.stop_loss) return { label: "SL HIT", color: "#FF3B30", bg: "rgba(255,59,48,0.12)", icon: "⛔" };
  if (!isLong && cmp >= trade.stop_loss) return { label: "SL HIT", color: "#FF3B30", bg: "rgba(255,59,48,0.12)", icon: "⛔" };
  const livePnl = isLong ? (cmp - trade.entry_price) * trade.quantity : (trade.entry_price - cmp) * trade.quantity;
  if (livePnl > 0) return { label: "IN PROFIT", color: "#00FF88", bg: "rgba(0,255,136,0.07)", icon: "📈", pnl: livePnl };
  if (livePnl < 0) return { label: "IN LOSS", color: "#FF3B30", bg: "rgba(255,59,48,0.07)", icon: "📉", pnl: livePnl };
  return { label: "BREAKEVEN", color: "#FFCC00", bg: "rgba(255,204,0,0.07)", icon: "⚖", pnl: 0 };
}

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, subvalue, icon: Icon, color, testId }) => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const displayColor = isLight
    ? color === '#00F0FF' ? '#0284C7'
      : color === '#00FF88' ? '#059669'
      : color === '#FFCC00' ? '#D97706'
      : color === '#FF3B30' ? '#DC2626'
      : color
    : color;

  return (
    <div className="terminal-card rounded-lg p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-4 h-4" style={{ color: displayColor }} strokeWidth={1.5} />
        <p className={`text-[10px] font-jetbrains tracking-[0.15em] uppercase font-bold ${isLight ? 'text-slate-600' : 'text-gray-400 dark:text-[#52525B]'}`}>{label}</p>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white font-jetbrains">{value}</p>
      {subvalue && <p className={`text-xs font-plex mt-1 ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>{subvalue}</p>}
    </div>
  );
};

// ── Insights Panel ────────────────────────────────────────────────────────────
const InsightsPanel = ({ insights }) => {
  if (!insights) return null;
  const { best_trade, worst_trade, improvement_tip, best_setup, top_mistake, daily_pnl } = insights;

  // Mini bar chart from daily_pnl
  const dailyEntries = Object.entries(daily_pnl || {});
  const maxAbs = Math.max(...dailyEntries.map(([, v]) => Math.abs(v)), 1);

  return (
    <div className="terminal-card rounded-lg p-4 space-y-4" data-testid="insights-panel">
      <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#A855F7] uppercase">🧠 Trading Insights</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Best trade */}
        {best_trade && (
          <div className="rounded-lg p-3 bg-[#00FF88]/[0.05] border border-[#00FF88]/15">
            <p className="text-[9px] font-jetbrains text-[#00FF88] uppercase tracking-wider mb-1">🏆 Best Trade</p>
            <p className="font-outfit font-bold text-gray-900 dark:text-white">{best_trade.stock_name}</p>
            <p className="font-jetbrains text-[#00FF88] font-bold text-sm">{fmtPnl(best_trade.pnl)}</p>
            <p className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">{fmtPct(best_trade.pnl_percentage)}</p>
          </div>
        )}

        {/* Worst trade */}
        {worst_trade && worst_trade.pnl < 0 && (
          <div className="rounded-lg p-3 bg-[#FF3B30]/[0.05] border border-[#FF3B30]/15">
            <p className="text-[9px] font-jetbrains text-[#FF3B30] uppercase tracking-wider mb-1">📉 Worst Trade</p>
            <p className="font-outfit font-bold text-gray-900 dark:text-white">{worst_trade.stock_name}</p>
            <p className="font-jetbrains text-[#FF3B30] font-bold text-sm">{fmtPnl(worst_trade.pnl)}</p>
            <p className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">{fmtPct(worst_trade.pnl_percentage)}</p>
          </div>
        )}

        {/* Best setup */}
        {best_setup && (
          <div className="rounded-lg p-3 bg-[#FFCC00]/[0.05] border border-[#FFCC00]/15">
            <p className="text-[9px] font-jetbrains text-[#FFCC00] uppercase tracking-wider mb-1">⚡ Best Setup</p>
            <p className="font-outfit font-bold text-gray-900 dark:text-white capitalize">{best_setup.name.replace("_", " ")}</p>
            <p className="font-jetbrains text-[#FFCC00] font-bold text-sm">{best_setup.win_rate}% Win Rate</p>
            <p className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">{best_setup.total} trades</p>
          </div>
        )}
      </div>

      {/* Improvement tip */}
      {improvement_tip && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-[#A855F7]/[0.05] border border-[#A855F7]/15">
          <Brain className="w-4 h-4 text-[#A855F7] shrink-0 mt-0.5" />
          <div>
            <p className="text-[9px] font-jetbrains text-[#A855F7] uppercase tracking-wider mb-0.5">
              Improvement Tip · #{top_mistake?.replace("_", " ")}
            </p>
            <p className="text-sm text-gray-700 dark:text-[#CBD5E1] font-plex">{improvement_tip}</p>
          </div>
        </div>
      )}

      {/* Day-by-day P&L sparkline */}
      {dailyEntries.length > 0 && (
        <div>
          <p className="text-[9px] font-jetbrains text-gray-400 dark:text-[#52525B] uppercase tracking-wider mb-2">Daily P&L (last {dailyEntries.length} days)</p>
          <div className="flex items-end gap-0.5 h-10">
            {dailyEntries.map(([day, val]) => {
              const pct = (Math.abs(val) / maxAbs) * 100;
              const isPos = val >= 0;
              return (
                <div key={day} className="flex-1 flex flex-col justify-end group relative" title={`${day}: ${fmtPnl(val)}`}>
                  <div
                    className="w-full rounded-sm transition-all hover:opacity-80"
                    style={{
                      height: `${Math.max(4, pct)}%`,
                      backgroundColor: isPos ? "#00FF88" : "#FF3B30",
                      opacity: 0.7
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Trade Card (replaces table row) ─────────────────────────────────────────
const TradeCard = ({ trade, livePrice, onClose, onView, onDelete }) => {
  const isActive = trade.status === "active";
  const isWin = trade.trade_result === "win";
  const isLoss = trade.trade_result === "loss";
  const isLong = trade.trade_type === "long";

  const cmp = (livePrice?.last_price && livePrice.last_price > 0) ? livePrice.last_price : (trade.entry_price || 0);
  const liveStatus = getLiveTradeStatus(trade, cmp);
  const livePnl = liveStatus?.pnl;

  const accentColor = isActive
    ? (liveStatus?.color || "#00F0FF")
    : isWin ? "#00FF88" : isLoss ? "#FF3B30" : "#FFCC00";

  return (
    <div
      className="terminal-card rounded-lg overflow-hidden group hover:border-gray-200 dark:border-white/10 transition-all"
      style={{ borderLeft: `3px solid ${accentColor}` }}
      data-testid={`trade-card-${trade.trade_id}`}
    >
      {/* Top: stock + badges */}
      <div className="p-4 pb-2">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-outfit font-bold text-gray-900 dark:text-white">{trade.stock_name}</span>
              <Badge className={`${isLong ? "bg-[#00FF88]/10 text-[#00FF88]" : "bg-[#FF3B30]/10 text-[#FF3B30]"} border-0 text-[10px] font-jetbrains rounded-sm`}>
                {isLong ? <ArrowUpRight className="w-3 h-3 mr-0.5" /> : <ArrowDownRight className="w-3 h-3 mr-0.5" />}
                {trade.trade_type.toUpperCase()}
              </Badge>
              {trade.drishti_linked && (
                <Badge className="bg-[#A855F7]/10 text-[#A855F7] border-0 text-[10px] font-jetbrains rounded-sm">
                  <Link2 className="w-2.5 h-2.5 mr-1" />Drishti
                </Badge>
              )}
              {trade.setup_category && (
                <Badge className="bg-[#FFCC00]/10 text-[#FFCC00] border-0 text-[10px] font-jetbrains rounded-sm capitalize">
                  {trade.setup_category.replace("_", " ")}
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains uppercase mt-0.5">
              {trade.instrument_type} {trade.timeframe && `/ ${trade.timeframe}`}
            </p>
          </div>

          {/* Status / P&L display */}
          <div className="text-right shrink-0">
            {isActive ? (
              <div>
                {liveStatus && (
                  <span
                    className="inline-block text-[9px] font-bold font-jetbrains px-2 py-0.5 rounded-full mb-1"
                    style={{ color: liveStatus.color, backgroundColor: liveStatus.bg }}
                  >
                    {liveStatus.icon} {liveStatus.label}
                  </span>
                )}
                {cmp && (
                  <div className="flex items-center gap-1 justify-end">
                    <span className="text-[9px] text-gray-400 dark:text-[#52525B] font-jetbrains">CMP</span>
                    <span className="text-sm font-bold font-jetbrains text-gray-900 dark:text-white">{fmtPrice(cmp)}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse" />
                  </div>
                )}
                {livePnl != null && (
                  <p className={`text-sm font-bold font-jetbrains ${livePnl >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]"}`}>
                    {fmtPnl(livePnl)} <span className="text-[9px] opacity-60">live</span>
                  </p>
                )}
              </div>
            ) : (
              <div className={`px-3 py-1.5 rounded-lg ${(trade.pnl || 0) >= 0 ? "bg-[#00FF88]/10" : "bg-[#FF3B30]/10"}`}>
                <p className="text-[9px] font-jetbrains text-gray-400 dark:text-[#52525B]">P&L</p>
                <p className={`text-base font-bold font-jetbrains ${(trade.pnl || 0) >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]"}`}>
                  {fmtPnl(trade.pnl)}
                </p>
                {trade.pnl_percentage != null && (
                  <p className={`text-[10px] font-jetbrains ${(trade.pnl_percentage || 0) >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]"}`}>
                    {fmtPct(trade.pnl_percentage)}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Price levels grid */}
        <div className="grid grid-cols-3 gap-2 mt-2">
          <div className="bg-gray-50 dark:bg-white/[0.02] rounded p-2">
            <p className="text-[8px] font-jetbrains text-gray-400 dark:text-[#52525B] uppercase">Entry</p>
            <p className={`text-xs font-bold font-jetbrains ${isLong ? "text-[#00FF88]" : "text-[#FF3B30]"}`}>{fmtPrice(trade.entry_price)}</p>
          </div>
          <div className="bg-gray-50 dark:bg-white/[0.02] rounded p-2">
            <p className="text-[8px] font-jetbrains text-gray-400 dark:text-[#52525B] uppercase">Stop Loss</p>
            <p className="text-xs font-bold font-jetbrains text-[#FF3B30]">{fmtPrice(trade.stop_loss)}</p>
          </div>
          <div className="bg-gray-50 dark:bg-white/[0.02] rounded p-2">
            <p className="text-[8px] font-jetbrains text-gray-400 dark:text-[#52525B] uppercase">Target</p>
            <p className="text-xs font-bold font-jetbrains text-[#00FF88]">{fmtPrice(trade.target)}</p>
          </div>
        </div>

        {/* Mistake pills */}
        {trade.mistake_tags?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {trade.mistake_tags.map(tag => (
              <span key={tag} className="text-[9px] font-plex px-1.5 py-0.5 rounded bg-[#FF3B30]/10 text-[#FF3B30]">
                {tag.replace("_", " ")}
              </span>
            ))}
          </div>
        )}

        {/* Improvement tip if mistake exists */}
        {trade.mistake_tags?.length > 0 && IMPROVEMENT_TIPS[trade.mistake_tags[0]] && (
          <div className="mt-2 flex items-start gap-1.5 p-1.5 rounded bg-[#A855F7]/[0.04] border border-[#A855F7]/10">
            <Brain className="w-3 h-3 text-[#A855F7] shrink-0 mt-0.5" />
            <p className="text-[10px] text-[#A855F7] font-plex leading-snug">{IMPROVEMENT_TIPS[trade.mistake_tags[0]]}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-100 dark:border-white/[0.04] flex items-center justify-between">
        <div className="flex items-center gap-1">
          {trade.risk_reward_ratio > 0 && (
            <span className="text-[10px] font-jetbrains text-[#00F0FF]">R:R 1:{trade.risk_reward_ratio}</span>
          )}
          {trade.confidence_score && (
            <span className="text-[10px] font-jetbrains text-gray-400 dark:text-[#52525B] ml-2">Conf: {trade.confidence_score}/10</span>
          )}
        </div>
        <div className="flex gap-1 items-center">
          <span className="text-[10px] font-jetbrains text-gray-400 dark:text-[#52525B] mr-1">
            {isActive ? "ACTIVE" : trade.trade_result?.toUpperCase() || "CLOSED"}
          </span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 dark:text-[#52525B] hover:text-gray-900 dark:text-white" onClick={() => onView(trade)} data-testid={`view-trade-${trade.trade_id}`}><Eye className="w-3.5 h-3.5" /></Button>
          {isActive && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#FF9500] hover:text-[#FFCC00]" onClick={() => onClose(trade)} data-testid={`close-trade-${trade.trade_id}`}><Target className="w-3.5 h-3.5" /></Button>}
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 dark:text-[#52525B] hover:text-[#FF3B30]" onClick={() => onDelete(trade.trade_id)} data-testid={`delete-trade-${trade.trade_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
    </div>
  );
};

// ── New Trade Form ────────────────────────────────────────────────────────────
const NewTradeForm = ({ onSubmit, onClose, recentAlerts }) => {
  const [form, setForm] = useState({
    stock_name: "", instrument_type: "equity", trade_type: "long",
    entry_price: "", stop_loss: "", target: "", quantity: "1",
    capital_used: "", entry_date: new Date().toISOString().slice(0, 16),
    setup_category: "", zone_score: "", mtf_confirmation: false,
    timeframe: "", pre_trade_checklist: [], entry_mindset: "",
    confidence_score: "", screenshot_url: "", notes: "",
    alert_id: "", drishti_linked: false
  });

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const rr = useMemo(() => {
    const e = parseFloat(form.entry_price), s = parseFloat(form.stop_loss), t = parseFloat(form.target);
    if (!e || !s || !t) return "--";
    const risk = Math.abs(e - s), reward = Math.abs(t - e);
    return risk > 0 ? `1:${(reward / risk).toFixed(1)}` : "--";
  }, [form.entry_price, form.stop_loss, form.target]);

  const toggleChecklist = (item) => {
    update("pre_trade_checklist",
      form.pre_trade_checklist.includes(item)
        ? form.pre_trade_checklist.filter(i => i !== item)
        : [...form.pre_trade_checklist, item]
    );
  };

  // Pre-fill from Drishti alert
  const linkAlert = (alertId) => {
    const alert = recentAlerts.find(a => a.alert_id === alertId);
    if (!alert) { update("alert_id", alertId); return; }
    setForm(p => ({
      ...p,
      alert_id: alertId,
      drishti_linked: true,
      stock_name: alert.script_name || p.stock_name,
      entry_price: alert.entry ? String(alert.entry) : p.entry_price,
      stop_loss: alert.stop_loss ? String(alert.stop_loss) : p.stop_loss,
      target: alert.target ? String(alert.target) : p.target,
      trade_type: alert.zone_type === "DEMAND" ? "long" : "short"
    }));
    toast.success(`Pre-filled from ${alert.script_name} alert`);
  };

  const handleSubmit = () => {
    if (!form.stock_name || !form.entry_price || !form.stop_loss || !form.target) {
      toast.error("Stock, Entry, SL and Target are required");
      return;
    }
    onSubmit({
      ...form,
      entry_price: parseFloat(form.entry_price),
      stop_loss: parseFloat(form.stop_loss),
      target: parseFloat(form.target),
      quantity: parseInt(form.quantity) || 1,
      capital_used: form.capital_used ? parseFloat(form.capital_used) : null,
      zone_score: form.zone_score ? parseFloat(form.zone_score) : null,
      confidence_score: form.confidence_score ? parseInt(form.confidence_score) : null,
      entry_date: new Date(form.entry_date).toISOString(),
      alert_id: form.alert_id || null,
      drishti_linked: form.drishti_linked
    });
  };

  return (
    <div className="space-y-5 pr-1">
      {/* Link Drishti Alert */}
      {recentAlerts?.length > 0 && (
        <div className="p-3 rounded-lg bg-[#A855F7]/[0.05] border border-[#A855F7]/15">
          <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#A855F7] uppercase mb-2">
            <Link2 className="w-3 h-3 inline mr-1" />Link Drishti Alert (auto-fills price levels)
          </p>
          <Select value={form.alert_id} onValueChange={linkAlert}>
            <SelectTrigger className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white">
              <SelectValue placeholder="Select an alert to pre-fill..." />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#0C0C10] border-gray-200 dark:border-white/[0.06]">
              {recentAlerts.map(a => (
                <SelectItem key={a.alert_id} value={a.alert_id} className="text-gray-900 dark:text-white">
                  {a.script_name} — {a.zone_type} @ {a.entry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.drishti_linked && (
            <p className="text-[10px] text-[#A855F7] font-plex mt-1">✓ Linked to Drishti alert</p>
          )}
        </div>
      )}

      {/* Core Trade Details */}
      <div>
        <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#00FF88] uppercase mb-3">TRADE DETAILS</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="col-span-2 md:col-span-1">
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Stock / Instrument *</Label>
            <Input value={form.stock_name} onChange={e => update("stock_name", e.target.value.toUpperCase())} placeholder="RELIANCE" className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-jetbrains mt-1" data-testid="trade-stock-input" />
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Type</Label>
            <Select value={form.instrument_type} onValueChange={v => update("instrument_type", v)}>
              <SelectTrigger className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1" data-testid="trade-instrument-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white dark:bg-[#0C0C10] border-gray-200 dark:border-white/[0.06]">
                {INSTRUMENTS.map(i => <SelectItem key={i} value={i} className="text-gray-900 dark:text-white capitalize">{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Direction *</Label>
            <div className="flex gap-2 mt-1">
              <Button type="button" size="sm" className={`flex-1 rounded-sm ${form.trade_type === "long" ? "bg-[#00FF88]/20 text-[#00FF88] border border-[#00FF88]/30" : "bg-gray-50 dark:bg-white/[0.03] text-[#52525B] border border-gray-200 dark:border-white/[0.06]"}`} onClick={() => update("trade_type", "long")} data-testid="trade-long-btn">
                <ArrowUpRight className="w-3.5 h-3.5 mr-1" /> Long
              </Button>
              <Button type="button" size="sm" className={`flex-1 rounded-sm ${form.trade_type === "short" ? "bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/30" : "bg-gray-50 dark:bg-white/[0.03] text-[#52525B] border border-gray-200 dark:border-white/[0.06]"}`} onClick={() => update("trade_type", "short")} data-testid="trade-short-btn">
                <ArrowDownRight className="w-3.5 h-3.5 mr-1" /> Short
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Price Levels */}
      <div>
        <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#00F0FF] uppercase mb-3">PRICE LEVELS</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Entry Price *</Label>
            <Input type="number" step="0.01" value={form.entry_price} onChange={e => update("entry_price", e.target.value)} placeholder="0.00" className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-jetbrains mt-1" data-testid="trade-entry-input" />
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Stop Loss *</Label>
            <Input type="number" step="0.01" value={form.stop_loss} onChange={e => update("stop_loss", e.target.value)} placeholder="0.00" className="bg-gray-50 dark:bg-white/[0.03] border-[#FF3B30]/20 text-gray-900 dark:text-white font-jetbrains mt-1" data-testid="trade-sl-input" />
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Target *</Label>
            <Input type="number" step="0.01" value={form.target} onChange={e => update("target", e.target.value)} placeholder="0.00" className="bg-gray-50 dark:bg-white/[0.03] border-[#00FF88]/20 text-gray-900 dark:text-white font-jetbrains mt-1" data-testid="trade-target-input" />
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">R:R Ratio</Label>
            <div className="h-9 mt-1 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-md flex items-center justify-center font-jetbrains text-[#00F0FF] font-bold text-sm">{rr}</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Quantity</Label>
            <Input type="number" value={form.quantity} onChange={e => update("quantity", e.target.value)} className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-jetbrains mt-1" data-testid="trade-qty-input" />
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Capital Used</Label>
            <Input type="number" value={form.capital_used} onChange={e => update("capital_used", e.target.value)} placeholder="Auto" className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-jetbrains mt-1" />
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Entry Date/Time</Label>
            <Input type="datetime-local" value={form.entry_date} onChange={e => update("entry_date", e.target.value)} className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-jetbrains mt-1 text-xs" data-testid="trade-date-input" />
          </div>
        </div>
      </div>

      {/* Analysis & Setup */}
      <div>
        <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#FFCC00] uppercase mb-3">ANALYSIS & SETUP</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Setup Category</Label>
            <Select value={form.setup_category} onValueChange={v => update("setup_category", v)}>
              <SelectTrigger className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1" data-testid="trade-setup-select"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent className="bg-white dark:bg-[#0C0C10] border-gray-200 dark:border-white/[0.06]">
                {SETUP_CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-gray-900 dark:text-white capitalize">{c.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Timeframe</Label>
            <Select value={form.timeframe} onValueChange={v => update("timeframe", v)}>
              <SelectTrigger className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent className="bg-white dark:bg-[#0C0C10] border-gray-200 dark:border-white/[0.06]">
                {TIMEFRAMES.map(t => <SelectItem key={t} value={t} className="text-gray-900 dark:text-white">{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Zone Score (1-10)</Label>
            <Input type="number" min="1" max="10" value={form.zone_score} onChange={e => update("zone_score", e.target.value)} placeholder="--" className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-jetbrains mt-1" />
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Confidence (1-10)</Label>
            <Input type="number" min="1" max="10" value={form.confidence_score} onChange={e => update("confidence_score", e.target.value)} placeholder="--" className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-jetbrains mt-1" data-testid="trade-confidence-input" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.mtf_confirmation} onChange={e => update("mtf_confirmation", e.target.checked)} className="rounded border-gray-200 dark:border-white/[0.06]" />
            <span className="text-sm text-gray-500 dark:text-[#A1A1AA] font-plex">MTF Confirmation</span>
          </label>
        </div>
      </div>

      {/* Pre-Trade Checklist */}
      <div>
        <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#FF9500] uppercase mb-3">PRE-TRADE CHECKLIST</p>
        <div className="grid grid-cols-2 gap-2">
          {PRE_TRADE_CHECKLIST_ITEMS.map(item => (
            <label key={item} className="flex items-center gap-2 cursor-pointer p-2 rounded bg-gray-50 dark:bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <input type="checkbox" checked={form.pre_trade_checklist.includes(item)} onChange={() => toggleChecklist(item)} className="rounded border-gray-200 dark:border-white/[0.06]" />
              <span className="text-xs text-gray-500 dark:text-[#A1A1AA] font-plex">{item}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Mindset & Notes */}
      <div>
        <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#A855F7] uppercase mb-3">MINDSET & NOTES</p>
        <div className="space-y-3">
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Entry Mindset</Label>
            <Textarea value={form.entry_mindset} onChange={e => update("entry_mindset", e.target.value)} placeholder="How are you feeling before this trade? Confident, anxious, neutral..." className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-plex mt-1 min-h-[60px] text-sm" data-testid="trade-mindset-input" />
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Screenshot URL</Label>
            <Input value={form.screenshot_url} onChange={e => update("screenshot_url", e.target.value)} placeholder="https://..." className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1" />
          </div>
          <div>
            <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Notes / Remarks</Label>
            <Textarea value={form.notes} onChange={e => update("notes", e.target.value)} placeholder="Additional notes about this trade..." className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-plex mt-1 min-h-[60px] text-sm" data-testid="trade-notes-input" />
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button onClick={handleSubmit} className="bg-[#00FF88] text-black font-bold hover:brightness-110 rounded-sm flex-1" data-testid="submit-trade-btn">
          <Plus className="w-4 h-4 mr-2" /> Log Trade
        </Button>
        <Button variant="ghost" onClick={onClose} className="text-gray-400 dark:text-[#52525B] hover:text-gray-900 dark:text-white">Cancel</Button>
      </div>
    </div>
  );
};

// ── Close Trade Form ──────────────────────────────────────────────────────────
const CloseTradeForm = ({ trade, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    exit_price: "", exit_date: new Date().toISOString().slice(0, 16),
    exit_reason: "", exit_mindset: "", emotional_score: "",
    mistake_tags: [], post_trade_learning: ""
  });
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleMistake = (tag) => {
    update("mistake_tags",
      form.mistake_tags.includes(tag)
        ? form.mistake_tags.filter(t => t !== tag)
        : [...form.mistake_tags, tag]
    );
  };

  const pnlPreview = useMemo(() => {
    const exit = parseFloat(form.exit_price);
    if (!exit || !trade) return null;
    const pnl = trade.trade_type === "long"
      ? (exit - trade.entry_price) * trade.quantity
      : (trade.entry_price - exit) * trade.quantity;
    return pnl;
  }, [form.exit_price, trade]);

  const handleSubmit = () => {
    if (!form.exit_price) { toast.error("Exit price required"); return; }
    onSubmit({
      exit_price: parseFloat(form.exit_price),
      exit_date: new Date(form.exit_date).toISOString(),
      exit_reason: form.exit_reason || null,
      exit_mindset: form.exit_mindset || null,
      emotional_score: form.emotional_score ? parseInt(form.emotional_score) : null,
      mistake_tags: form.mistake_tags.length > 0 ? form.mistake_tags : null,
      post_trade_learning: form.post_trade_learning || null,
      status: "closed"
    });
  };

  return (
    <div className="space-y-5 pr-1">
      <div className="terminal-card rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-outfit font-bold text-gray-900 dark:text-white">{trade.stock_name}</h3>
            <Badge className={`mt-1 ${trade.trade_type === "long" ? "bg-[#00FF88]/10 text-[#00FF88]" : "bg-[#FF3B30]/10 text-[#FF3B30]"} border-0 text-xs`}>{trade.trade_type.toUpperCase()}</Badge>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 dark:text-[#52525B] font-jetbrains">Entry</p>
            <p className="text-lg font-bold font-jetbrains text-gray-900 dark:text-white">{trade.entry_price}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Exit Price *</Label>
          <Input type="number" step="0.01" value={form.exit_price} onChange={e => update("exit_price", e.target.value)} placeholder="0.00" className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-jetbrains mt-1" data-testid="close-exit-price-input" />
        </div>
        <div>
          <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Exit Date/Time</Label>
          <Input type="datetime-local" value={form.exit_date} onChange={e => update("exit_date", e.target.value)} className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-jetbrains mt-1 text-xs" />
        </div>
      </div>

      {pnlPreview !== null && (
        <div className={`terminal-card rounded-lg p-4 ${pnlPreview >= 0 ? "border-[#00FF88]/20" : "border-[#FF3B30]/20"}`}>
          <p className="text-[10px] font-jetbrains text-gray-400 dark:text-[#52525B] tracking-[0.15em] uppercase">ESTIMATED P&L</p>
          <p className={`text-2xl font-bold font-jetbrains ${pnlPreview >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]"}`}>
            {pnlPreview >= 0 ? "+" : ""}{pnlPreview.toFixed(2)}
          </p>
        </div>
      )}

      <div>
        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Exit Reason</Label>
        <Select value={form.exit_reason} onValueChange={v => update("exit_reason", v)}>
          <SelectTrigger className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1"><SelectValue placeholder="Select reason..." /></SelectTrigger>
          <SelectContent className="bg-white dark:bg-[#0C0C10] border-gray-200 dark:border-white/[0.06]">
            {EXIT_REASONS.map(r => <SelectItem key={r} value={r} className="text-gray-900 dark:text-white capitalize">{r.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Exit Mindset</Label>
        <Textarea value={form.exit_mindset} onChange={e => update("exit_mindset", e.target.value)} placeholder="How did you feel exiting?" className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-plex mt-1 min-h-[50px] text-sm" />
      </div>

      <div>
        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Emotional Score (1-10)</Label>
        <Input type="number" min="1" max="10" value={form.emotional_score} onChange={e => update("emotional_score", e.target.value)} placeholder="How calm were you?" className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-jetbrains mt-1" />
      </div>

      <div>
        <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#FF3B30] uppercase mb-2">MISTAKE TAGS (select all that apply)</p>
        <div className="flex flex-wrap gap-2">
          {MISTAKE_TAGS.map(tag => (
            <button key={tag} onClick={() => toggleMistake(tag)}
              className={`px-3 py-1.5 rounded-sm text-xs font-plex transition-all ${form.mistake_tags.includes(tag) ? "bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/30" : "bg-gray-50 dark:bg-white/[0.03] text-[#52525B] border border-gray-200 dark:border-white/[0.06] hover:text-[#A1A1AA]"}`}>
              {tag.replace("_", " ")}
            </button>
          ))}
        </div>
        {/* Show improvement tip for first selected mistake */}
        {form.mistake_tags.length > 0 && IMPROVEMENT_TIPS[form.mistake_tags[0]] && (
          <div className="mt-2 flex items-start gap-2 p-2.5 rounded bg-[#A855F7]/[0.05] border border-[#A855F7]/15">
            <Brain className="w-3.5 h-3.5 text-[#A855F7] shrink-0 mt-0.5" />
            <p className="text-xs text-[#A855F7] font-plex">{IMPROVEMENT_TIPS[form.mistake_tags[0]]}</p>
          </div>
        )}
      </div>

      <div>
        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Post-Trade Learning</Label>
        <Textarea value={form.post_trade_learning} onChange={e => update("post_trade_learning", e.target.value)} placeholder="What did you learn from this trade?" className="bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white font-plex mt-1 min-h-[60px] text-sm" data-testid="close-learning-input" />
      </div>

      <div className="flex gap-3 pt-2">
        <Button onClick={handleSubmit} className="bg-[#FF9500] text-black font-bold hover:brightness-110 rounded-sm flex-1" data-testid="close-trade-submit-btn">Close Trade</Button>
        <Button variant="ghost" onClick={onClose} className="text-gray-400 dark:text-[#52525B] hover:text-gray-900 dark:text-white">Cancel</Button>
      </div>
    </div>
  );
};

// ── Trade Detail View ─────────────────────────────────────────────────────────
const TradeDetailView = ({ trade, onClose }) => {
  if (!trade) return null;

  const isLong = trade.trade_type === "long";
  const dur = tradeDuration(trade.entry_date, trade.exit_date);

  const sections = [
    { label: "Entry Price", value: fmtPrice(trade.entry_price), color: "text-gray-900 dark:text-white" },
    { label: "Stop Loss", value: fmtPrice(trade.stop_loss), color: "text-[#FF3B30]" },
    { label: "Target", value: fmtPrice(trade.target), color: "text-[#00FF88]" },
    { label: "R:R Planned", value: trade.risk_reward_ratio ? `1:${trade.risk_reward_ratio}` : "--", color: "text-[#00F0FF]" },
    { label: "Quantity", value: trade.quantity, color: "text-gray-900 dark:text-white" },
    { label: "Capital", value: `₹${(trade.capital_used || 0).toLocaleString()}`, color: "text-gray-900 dark:text-white" },
  ];
  if (trade.exit_price) {
    sections.push({ label: "Exit Price", value: fmtPrice(trade.exit_price), color: "text-[#FFCC00]" });
    sections.push({ label: "P&L", value: `${(trade.pnl || 0) >= 0 ? "+" : ""}₹${(trade.pnl || 0).toFixed(2)}`, color: (trade.pnl || 0) >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]" });
  }

  // R:R achieved
  let rrAchieved = null;
  if (trade.exit_price && trade.entry_price && trade.stop_loss) {
    const risk = Math.abs(trade.entry_price - trade.stop_loss);
    const reward = Math.abs(trade.exit_price - trade.entry_price);
    rrAchieved = risk > 0 ? (reward / risk).toFixed(2) : null;
  }

  return (
    <div className="space-y-5 pr-1">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white font-outfit">{trade.stock_name}</h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge className={`${isLong ? "bg-[#00FF88]/10 text-[#00FF88]" : "bg-[#FF3B30]/10 text-[#FF3B30]"} border-0 text-xs`}>{trade.trade_type.toUpperCase()}</Badge>
            <Badge className={`${trade.status === "active" ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "bg-gray-100 dark:bg-white/5 text-[#A1A1AA]"} border-0 text-xs`}>{trade.status.toUpperCase()}</Badge>
            {trade.setup_category && <Badge className="bg-[#FFCC00]/10 text-[#FFCC00] border-0 text-xs capitalize">{trade.setup_category.replace("_", " ")}</Badge>}
            {trade.drishti_linked && <Badge className="bg-[#A855F7]/10 text-[#A855F7] border-0 text-xs"><Link2 className="w-2.5 h-2.5 mr-1" />Drishti</Badge>}
          </div>
        </div>
        {trade.status === "closed" && (
          <div className={`text-right px-4 py-3 rounded-lg ${(trade.pnl || 0) >= 0 ? "bg-[#00FF88]/10" : "bg-[#FF3B30]/10"}`}>
            <p className="text-xs text-gray-400 dark:text-[#52525B] font-jetbrains">Final P&L</p>
            <p className={`text-2xl font-bold font-jetbrains ${(trade.pnl || 0) >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]"}`}>
              {(trade.pnl || 0) >= 0 ? "+" : ""}₹{(trade.pnl || 0).toFixed(2)}
            </p>
            {trade.pnl_percentage != null && <p className={`text-xs font-jetbrains ${(trade.pnl_percentage || 0) >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]"}`}>{fmtPct(trade.pnl_percentage)}</p>}
          </div>
        )}
      </div>

      {/* Performance Summary (closed trades) */}
      {trade.status === "closed" && (
        <div className="terminal-card rounded-lg p-4">
          <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#00F0FF] uppercase mb-3">TRADE PERFORMANCE</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <p className="text-[9px] font-jetbrains text-gray-400 dark:text-[#52525B] uppercase">Result</p>
              <p className={`text-sm font-bold font-jetbrains capitalize ${trade.trade_result === "win" ? "text-[#00FF88]" : trade.trade_result === "loss" ? "text-[#FF3B30]" : "text-[#FFCC00]"}`}>{trade.trade_result || "—"}</p>
            </div>
            <div>
              <p className="text-[9px] font-jetbrains text-gray-400 dark:text-[#52525B] uppercase">R:R Achieved</p>
              <p className="text-sm font-bold font-jetbrains text-[#00F0FF]">{rrAchieved ? `1:${rrAchieved}` : "—"}</p>
            </div>
            <div>
              <p className="text-[9px] font-jetbrains text-gray-400 dark:text-[#52525B] uppercase">Duration</p>
              <p className="text-sm font-bold font-jetbrains text-gray-900 dark:text-white">{dur || "—"}</p>
            </div>
            <div>
              <p className="text-[9px] font-jetbrains text-gray-400 dark:text-[#52525B] uppercase">Exit Reason</p>
              <p className="text-sm font-bold font-jetbrains text-gray-900 dark:text-white capitalize">{trade.exit_reason?.replace("_", " ") || "—"}</p>
            </div>
          </div>
        </div>
      )}

      {/* Price Grid */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
        {sections.map((s) => (
          <div key={s.label} className="bg-gray-50 dark:bg-white/[0.02] rounded p-3">
            <p className="text-[10px] font-jetbrains text-gray-400 dark:text-[#52525B] tracking-[0.1em] uppercase">{s.label}</p>
            <p className={`text-sm font-bold font-jetbrains ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Mindset */}
      {(trade.entry_mindset || trade.exit_mindset) && (
        <div className="terminal-card rounded-lg p-4">
          <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#A855F7] uppercase mb-3">MINDSET</p>
          {trade.entry_mindset && <div className="mb-2"><p className="text-xs text-gray-400 dark:text-[#52525B]">Entry:</p><p className="text-sm text-gray-500 dark:text-[#A1A1AA] font-plex">{trade.entry_mindset}</p></div>}
          {trade.exit_mindset && <div><p className="text-xs text-gray-400 dark:text-[#52525B]">Exit:</p><p className="text-sm text-gray-500 dark:text-[#A1A1AA] font-plex">{trade.exit_mindset}</p></div>}
        </div>
      )}

      {/* Checklist */}
      {trade.pre_trade_checklist?.length > 0 && (
        <div className="terminal-card rounded-lg p-4">
          <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#FF9500] uppercase mb-2">PRE-TRADE CHECKLIST</p>
          <div className="flex flex-wrap gap-2">
            {trade.pre_trade_checklist.map((item) => (
              <Badge key={item} className="bg-[#00FF88]/10 text-[#00FF88] border-0 text-[10px]"><CheckSquare className="w-3 h-3 mr-1" />{item}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Mistakes + Improvement */}
      {trade.mistake_tags?.length > 0 && (
        <div className="terminal-card rounded-lg p-4 space-y-3">
          <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#FF3B30] uppercase">MISTAKES TAGGED</p>
          <div className="flex flex-wrap gap-2">
            {trade.mistake_tags.map((tag) => (
              <Badge key={tag} className="bg-[#FF3B30]/10 text-[#FF3B30] border-0 text-[10px] capitalize">{tag.replace("_", " ")}</Badge>
            ))}
          </div>
          {/* Improvement tips for ALL tagged mistakes */}
          <div className="space-y-2 mt-2">
            {trade.mistake_tags.filter(t => IMPROVEMENT_TIPS[t]).map(tag => (
              <div key={tag} className="flex items-start gap-2 p-2.5 rounded bg-[#A855F7]/[0.05] border border-[#A855F7]/15">
                <Brain className="w-3.5 h-3.5 text-[#A855F7] shrink-0 mt-0.5" />
                <div>
                  <p className="text-[9px] font-jetbrains text-[#A855F7] uppercase mb-0.5">{tag.replace("_", " ")}</p>
                  <p className="text-xs text-gray-700 dark:text-[#CBD5E1] font-plex">{IMPROVEMENT_TIPS[tag]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Post-trade learning */}
      {trade.post_trade_learning && (
        <div className="terminal-card rounded-lg p-4">
          <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#00F0FF] uppercase mb-2">POST-TRADE LEARNING</p>
          <p className="text-sm text-gray-500 dark:text-[#A1A1AA] font-plex">{trade.post_trade_learning}</p>
        </div>
      )}

      {/* Mentor review */}
      {trade.mentor_comments && (
        <div className="terminal-card rounded-lg p-4 border-[#FFCC00]/20">
          <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#FFCC00] uppercase mb-2">MENTOR REVIEW</p>
          <p className="text-sm text-gray-500 dark:text-[#A1A1AA] font-plex">{trade.mentor_comments}</p>
        </div>
      )}

      {/* Screenshot */}
      {trade.screenshot_url && (
        <div className="terminal-card rounded-lg p-4">
          <p className="font-jetbrains text-[10px] tracking-[0.2em] text-gray-400 dark:text-[#52525B] uppercase mb-2">CHART SCREENSHOT</p>
          <img src={trade.screenshot_url} alt="Trade chart" className="w-full rounded border border-gray-200 dark:border-white/[0.06]" />
        </div>
      )}

      {trade.notes && (
        <div className="terminal-card rounded-lg p-4">
          <p className="font-jetbrains text-[10px] tracking-[0.2em] text-gray-400 dark:text-[#52525B] uppercase mb-2">NOTES</p>
          <p className="text-sm text-gray-500 dark:text-[#A1A1AA] font-plex">{trade.notes}</p>
        </div>
      )}

      <Button variant="ghost" onClick={onClose} className="w-full text-gray-400 dark:text-[#52525B] hover:text-gray-900 dark:text-white">Close</Button>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const TradingJournalPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const [hasAccess, setHasAccess] = useState(Boolean(isAdmin || user?.has_drishti_access === true));

  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showNewTrade, setShowNewTrade] = useState(false);
  const [closingTrade, setClosingTrade] = useState(null);
  const [viewingTrade, setViewingTrade] = useState(null);
  const [livePrices, setLivePrices] = useState({});
  const [recentAlerts, setRecentAlerts] = useState([]);

  useEffect(() => {
    if (isAdmin) {
      setHasAccess(true);
      return;
    }
    axios.get(`${API}/drishti/subscription-status`, { withCredentials: true })
      .then(res => {
        setHasAccess(Boolean(res.data?.has_access));
      })
      .catch(() => {
        setHasAccess(false);
      });
  }, [isAdmin, user]);

  const fetchData = useCallback(async () => {
    try {
      const [tradesRes, statsRes, insightsRes] = await Promise.all([
        axios.get(`${API}/journal/trades`, { withCredentials: true }),
        axios.get(`${API}/journal/stats`, { withCredentials: true }),
        axios.get(`${API}/journal/insights`, { withCredentials: true }),
      ]);
      setTrades(asArray(tradesRes.data, "trades"));
      setStats(statsRes.data);
      setInsights(insightsRes.data);
    } catch (err) {
      console.error("Journal fetch error:", err); // eslint-disable-line no-console
    } finally { setLoading(false); }
  }, []);

  // Fetch Drishti alerts for linking
  useEffect(() => {
    if (hasAccess || isAdmin) {
      axios.get(`${API}/drishti/alerts`, { withCredentials: true })
        .then(res => setRecentAlerts(asArray(res.data, "alerts").slice(0, 20)))
        .catch(() => {});
    }
  }, [hasAccess, isAdmin]);

  useEffect(() => {
    if (hasAccess || isAdmin) {
      fetchData();
    }
  }, [fetchData, hasAccess, isAdmin]);

  // Fetch live prices for active trades
  const fetchLivePrices = useCallback(async (tradesList) => {
    const activeSymbols = [...new Set(
      tradesList.filter(t => t.status === "active").map(t => t.stock_name).filter(Boolean)
    )];
    if (activeSymbols.length === 0) return;
    const chunkSize = 20;
    for (let i = 0; i < activeSymbols.length; i += chunkSize) {
      const chunk = activeSymbols.slice(i, i + chunkSize);
      try {
        const res = await axios.get(`${API}/market/ltp?symbols=${encodeURIComponent(chunk.join(","))}`);
        if (res.data?.data) {
          setLivePrices(prev => ({
            ...prev,
            ...res.data.data
          }));
        }
      } catch (err) { console.error("Error fetching live prices chunk:", err); }
    }
  }, []);

  useEffect(() => {
    if ((hasAccess || isAdmin) && trades.length > 0) {
      fetchLivePrices(trades);
    }
  }, [trades, fetchLivePrices, hasAccess, isAdmin]);

  // Auto-refresh live prices every 15s
  useEffect(() => {
    if (!hasAccess && !isAdmin) return;
    const iv = setInterval(() => {
      if (trades.some(t => t.status === "active")) fetchLivePrices(trades);
    }, 15000);
    return () => clearInterval(iv);
  }, [trades, fetchLivePrices, hasAccess, isAdmin]);

  const createTrade = async (data) => {
    try {
      await axios.post(`${API}/journal/trades`, data, { withCredentials: true });
      toast.success("Trade logged successfully");
      setShowNewTrade(false);
      fetchData();
    } catch (err) { toast.error("Failed to log trade"); }
  };

  const closeTrade = async (data) => {
    try {
      await axios.put(`${API}/journal/trades/${closingTrade.trade_id}`, data, { withCredentials: true });
      toast.success("Trade closed");
      setClosingTrade(null);
      fetchData();
    } catch (err) { toast.error("Failed to close trade"); }
  };

  const deleteTrade = async (tradeId) => {
    try {
      await axios.delete(`${API}/journal/trades/${tradeId}`, { withCredentials: true });
      toast.success("Trade deleted");
      fetchData();
    } catch (err) { toast.error("Failed to delete trade"); }
  };

  const filteredTrades = useMemo(() => {
    if (filter === "all") return trades;
    return trades.filter(t => t.status === filter);
  }, [trades, filter]);

  const activeTrades = trades.filter(t => t.status === "active");
  const hasLiveData = Object.keys(livePrices).length > 0;

  if (!hasAccess && !isAdmin) {
    return (
      <DashboardLayout title="Trade Journal">
        <DrishtiPaywallGate
          featureName="Institutional Trade Journal"
          onAccessGranted={() => {
            setHasAccess(true);
            fetchData();
          }}
        />
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout title="Trade Journal">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Trade Journal">
      <div className="space-y-5" data-testid="trading-journal-page">

        {/* ═══ Stats Overview ═══ */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="journal-stats">
          <StatCard label="TOTAL TRADES" value={stats?.total_trades || 0} icon={BookMarked} color="#00F0FF" testId="stat-total-trades" />
          <StatCard label="WIN RATE" value={`${stats?.win_rate || 0}%`} icon={Trophy} color="#00FF88" testId="stat-win-rate" />
          <StatCard label="TOTAL P&L" value={`₹${(stats?.total_pnl || 0).toLocaleString()}`} icon={DollarSign} color={(stats?.total_pnl || 0) >= 0 ? "#00FF88" : "#FF3B30"} testId="stat-total-pnl" />
          <StatCard label="AVG R:R" value={stats?.avg_risk_reward || "--"} icon={Target} color="#00F0FF" testId="stat-avg-rr" />
          <StatCard label="ACTIVE" value={stats?.active_trades || 0} icon={Activity} color="#FFCC00" testId="stat-active"
            subvalue={hasLiveData && activeTrades.length > 0 ? "Live prices on" : undefined} />
          <StatCard label="ROI" value={`${stats?.roi_percentage || 0}%`} icon={Percent} color={(stats?.roi_percentage || 0) >= 0 ? "#00FF88" : "#FF3B30"} testId="stat-roi" />
        </div>

        {/* ═══ Win/Loss Bar ═══ */}
        {(stats?.closed_trades || 0) > 0 && (
          <div className="terminal-card rounded-lg p-4" data-testid="win-loss-bar">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-400 dark:text-[#52525B] font-jetbrains">{stats.wins}W / {stats.losses}L / {stats.breakeven}BE</span>
              <span className="text-xs font-jetbrains font-bold text-[#00FF88]">{stats.win_rate}% Win Rate</span>
            </div>
            <div className="flex h-3 rounded-full overflow-hidden gap-0.5">
              {stats.wins > 0 && <div className="bg-[#00FF88] rounded-l-full" style={{ width: `${(stats.wins / stats.closed_trades) * 100}%` }} />}
              {stats.breakeven > 0 && <div className="bg-[#FFCC00]" style={{ width: `${(stats.breakeven / stats.closed_trades) * 100}%` }} />}
              {stats.losses > 0 && <div className="bg-[#FF3B30] rounded-r-full" style={{ width: `${(stats.losses / stats.closed_trades) * 100}%` }} />}
            </div>
          </div>
        )}

        {/* ═══ Insights Panel ═══ */}
        {insights && insights.total_closed > 0 && <InsightsPanel insights={insights} />}

        {/* ═══ Setup Performance ═══ */}
        {stats?.setup_performance && Object.keys(stats.setup_performance).length > 0 && (
          <div className="terminal-card rounded-lg p-4" data-testid="setup-performance">
            <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#FFCC00] uppercase mb-3">SETUP PERFORMANCE</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(stats.setup_performance).map(([cat, data]) => (
                <div key={cat} className="bg-gray-50 dark:bg-white/[0.02] rounded p-3">
                  <p className="text-xs font-outfit text-gray-900 dark:text-white capitalize mb-1">{cat.replace("_", " ")}</p>
                  <div className="flex items-baseline gap-2">
                    <span className="font-jetbrains text-sm font-bold text-gray-900 dark:text-white">{data.total}</span>
                    <span className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">trades</span>
                  </div>
                  <p className={`text-xs font-jetbrains mt-1 ${data.pnl >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]"}`}>
                    {data.pnl >= 0 ? "+" : ""}₹{data.pnl.toFixed(0)} ({data.total > 0 ? Math.round((data.wins / data.total) * 100) : 0}% WR)
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ Common Mistakes ═══ */}
        {stats?.mistake_frequency && Object.keys(stats.mistake_frequency).length > 0 && (
          <div className="terminal-card rounded-lg p-4" data-testid="mistake-analysis">
            <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#FF3B30] uppercase mb-3">COMMON MISTAKES</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.mistake_frequency).sort((a, b) => b[1] - a[1]).map(([tag, count]) => (
                <Badge key={tag} className="bg-[#FF3B30]/10 text-[#FF3B30] border-0 text-xs capitalize">
                  <AlertTriangle className="w-3 h-3 mr-1" />{tag.replace("_", " ")} ({count})
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* ═══ Toolbar ═══ */}
        <div className="flex items-center justify-between flex-wrap gap-3" data-testid="journal-toolbar">
          <div className="flex gap-1 flex-wrap">
            {["all", "active", "closed"].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-sm text-sm font-plex capitalize transition-all ${filter === f ? "bg-white/[0.06] text-gray-900 dark:text-white border border-gray-200 dark:border-white/10" : "text-[#52525B] hover:text-[#A1A1AA]"}`}
                data-testid={`filter-${f}`}>
                {f} {f === "all" ? `(${trades.length})` : `(${trades.filter(t => t.status === f).length})`}
              </button>
            ))}
          </div>
          <Dialog open={showNewTrade} onOpenChange={setShowNewTrade}>
            <DialogTrigger asChild>
              <Button className="bg-[#00FF88] text-black font-bold hover:brightness-110 rounded-sm" data-testid="new-trade-btn">
                <Plus className="w-4 h-4 mr-2" /> New Trade
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white dark:bg-[#0C0C10] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white max-w-2xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle className="font-outfit text-lg">Log New Trade</DialogTitle>
              </DialogHeader>
              <NewTradeForm onSubmit={createTrade} onClose={() => setShowNewTrade(false)} recentAlerts={recentAlerts} />
            </DialogContent>
          </Dialog>
        </div>

        {/* ═══ Trade Cards ═══ */}
        {filteredTrades.length === 0 ? (
          <div className="terminal-card rounded-lg p-12 text-center" data-testid="trades-table">
            <BookMarked className="w-10 h-10 text-gray-400 dark:text-[#52525B] mx-auto mb-3" />
            <p className="text-gray-400 dark:text-[#52525B] font-plex">No trades logged yet</p>
            <p className="text-xs text-gray-400 dark:text-[#52525B] font-plex mt-1">Click "New Trade" to start your journal</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="trades-table">
            {filteredTrades.map(trade => (
              <TradeCard
                key={trade.trade_id}
                trade={trade}
                livePrice={livePrices[trade.stock_name]}
                onClose={setClosingTrade}
                onView={setViewingTrade}
                onDelete={deleteTrade}
              />
            ))}
          </div>
        )}

        {/* ═══ Close Trade Dialog ═══ */}
        <Dialog open={!!closingTrade} onOpenChange={(open) => !open && setClosingTrade(null)}>
          <DialogContent className="bg-white dark:bg-[#0C0C10] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white max-w-lg max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="font-outfit text-lg">Close Trade</DialogTitle>
            </DialogHeader>
            {closingTrade && <CloseTradeForm trade={closingTrade} onSubmit={closeTrade} onClose={() => setClosingTrade(null)} />}
          </DialogContent>
        </Dialog>

        {/* ═══ View Trade Dialog ═══ */}
        <Dialog open={!!viewingTrade} onOpenChange={(open) => !open && setViewingTrade(null)}>
          <DialogContent className="bg-white dark:bg-[#0C0C10] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="font-outfit text-lg">Trade Details</DialogTitle>
            </DialogHeader>
            {viewingTrade && <TradeDetailView trade={viewingTrade} onClose={() => setViewingTrade(null)} />}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TradingJournalPage;
